import { Toast } from "@hatch-digital-nl/bravilor-storybook";
import { JSX, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RouteEnum } from "../../enums/RouteEnum";
import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { useReserveRefurbishedProductMutation } from "../../api/refurbished";
import { clearCartToast, showCartToast } from "../../slices/cartToastSlice";
import { useStringTranslation } from "../../hooks/useStringTranslation";

const TOAST_DURATION_MS = 5000;

export const CartToastContainer = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useAppSelector((state) => state.cartToast.toast);
    const { t } = useStringTranslation();
    const { data: userData } = useGetUserInfoQuery();
    const [reserveProduct] = useReserveRefurbishedProductMutation();

    useEffect(() => {
        if (!toast) {
            return;
        }

        const timeout = setTimeout(() => {
            dispatch(clearCartToast());
        }, TOAST_DURATION_MS);

        return () => clearTimeout(timeout);
    }, [toast, dispatch]);

    if (!toast) {
        return null;
    }

    const handleUndo = async (): Promise<void> => {
        if (!toast.undoProductId) {
            return;
        }

        dispatch(clearCartToast());

        try {
            await reserveProduct(toast.undoProductId).unwrap();
        } catch {
            dispatch(
                showCartToast({
                    variant: "error",
                    message: t("cart-reserve-failed"),
                }),
            );
        }
    };

    const handleClose = (): void => {
        dispatch(clearCartToast());
    };

    const handleViewCart = (): void => {
        if (!userData?.refurbishedShopEnabled) {
            dispatch(clearCartToast());
            return;
        }

        dispatch(clearCartToast());
        navigate(RouteEnum.REFURBISHED_CART);
    };

    const handleAction = (): void => {
        if (toast.actionType === "viewCart") {
            handleViewCart();
            return;
        }

        if (toast.actionType === "undo" && toast.undoProductId) {
            void handleUndo();
        }
    };

    const hasAction =
        toast.actionType === "viewCart" ||
        (toast.actionType === "undo" && toast.undoProductId !== undefined);

    return (
        <div className="c-cart-toast">
            <Toast
                variant={toast.variant}
                message={toast.message}
                actionLabel={toast.actionLabel}
                onAction={hasAction ? handleAction : undefined}
                onClose={handleClose}
                showIcon={toast.showIcon ?? true}
            />
        </div>
    );
};
